'use client';

import clsx from 'clsx';
import { animate } from 'motion';
import { useInView, useReducedMotion } from 'motion/react';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';

import type { StatFormat } from './content/types';
import { Container, SectionLabel } from './primitives';

function formatValue(value: number, format: StatFormat) {
  const decimals = Number.isInteger(value) ? 0 : 1;
  const n = value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
  switch (format) {
    case 'money-m':
      return { prefix: '$', number: n, unit: 'M' };
    case 'percent':
      return { prefix: '', number: n, unit: '%' };
    case 'days':
      return { prefix: '', number: n, unit: value === 1 ? ' day' : ' days' };
    default:
      return { prefix: '', number: n, unit: '' };
  }
}

function Figure({ value, format, tone }: { value: number; format: StatFormat; tone: 'ink' | 'white' }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: '-10% 0px' });
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (reduce) {
      setShown(value);
      return;
    }
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.6,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setShown(Number.isInteger(value) ? Math.round(v) : Math.round(v * 10) / 10),
    });
    return () => controls.stop();
  }, [inView, reduce, value]);

  const f = formatValue(shown, format);
  const finalF = formatValue(value, format);

  return (
    <span
      ref={ref}
      className={clsx(
        'font-display font-condensed tabular block text-[clamp(3.5rem,6.5vw,5.75rem)] leading-[0.88] font-extrabold tracking-[-0.03em]',
        tone === 'ink' ? 'text-ink-900' : 'text-white',
      )}
    >
      <span className="sr-only">
        {finalF.prefix}
        {finalF.number}
        {finalF.unit}
      </span>
      <span aria-hidden="true">
        {f.prefix}
        {f.number}
        <span className={clsx('text-[0.5em] tracking-[-0.02em]', tone === 'ink' ? 'text-muted' : 'text-white/85')}>
          {f.unit}
        </span>
      </span>
    </span>
  );
}

const TILE = 'rounded-[1.75rem] p-7 sm:p-8';

function StatTile({
  row,
  tone,
  className,
}: {
  row: { value: number; format: StatFormat; label: string };
  tone: 'white' | 'ink' | 'beacon';
  className?: string;
}) {
  return (
    <div
      className={clsx(
        TILE,
        'flex min-h-[13rem] flex-col justify-between',
        tone === 'white' && 'bg-white text-ink-900',
        tone === 'ink' && 'on-ink bg-ink-950 text-white',
        tone === 'beacon' && 'on-ink beacon-gradient text-white',
        className,
      )}
    >
      <Figure value={row.value} format={row.format} tone={tone === 'white' ? 'ink' : 'white'} />
      <p
        className={clsx(
          'mt-8 max-w-[16rem] text-pretty text-[0.95rem] leading-snug',
          tone === 'white' ? 'text-muted' : 'text-white',
        )}
      >
        {row.label}
      </p>
    </div>
  );
}

export function Bento({
  label,
  heading,
  intro,
  rows,
  statesHref,
  statesCount,
}: {
  label: string;
  heading: string;
  intro: string;
  rows: { value: number; format: StatFormat; label: string }[];
  statesHref: string;
  statesCount: number;
}) {
  const [a, b, c, d] = rows;
  return (
    <section aria-labelledby="bento-heading">
      <Container className="pt-6 pb-20 sm:pb-28">
        <div className="grid grid-cols-1 gap-3 sm:gap-4 lg:grid-cols-12">
          <div className={clsx(TILE, 'on-ink relative overflow-hidden bg-ink-950 text-white lg:col-span-8 lg:row-span-2 lg:p-12')}>
            <div aria-hidden="true" className="beacon-glow absolute inset-0 opacity-70" />
            <div className="relative flex h-full flex-col">
              <SectionLabel tone="white">{label}</SectionLabel>
              <h2
                id="bento-heading"
                className="font-display mt-4 max-w-[18ch] text-balance text-[clamp(2rem,4.2vw,3.5rem)] leading-[1.02] font-bold tracking-[-0.03em]"
              >
                {heading}
              </h2>
              <p className="mt-6 max-w-[32rem] text-pretty text-lg leading-relaxed text-mist">{intro}</p>
              <div className="mt-10 lg:mt-auto lg:pt-12">
                <a
                  href="#process"
                  className="inline-flex items-center rounded-full text-[0.95rem] font-semibold text-white underline decoration-white/40 underline-offset-[6px] transition-colors hover:decoration-beacon-400 lg:min-h-12 lg:bg-white lg:px-6 lg:text-ink-900 lg:no-underline lg:hover:bg-frost-100"
                >
                  See how we fix it
                </a>
              </div>
            </div>
          </div>

          {a ? <StatTile row={a} tone="beacon" className="lg:col-span-4" /> : null}
          {b ? <StatTile row={b} tone="white" className="lg:col-span-4" /> : null}
          {c ? <StatTile row={c} tone="white" className="lg:col-span-4" /> : null}
          {d ? <StatTile row={d} tone="ink" className="lg:col-span-4" /> : null}

          <Link
            href={statesHref}
            className={clsx(
              TILE,
              'group flex min-h-[13rem] flex-col justify-between bg-white text-ink-900 transition-colors hover:bg-beacon-100 lg:col-span-4',
            )}
          >
            <span className="font-display text-[clamp(1.6rem,2.4vw,2rem)] leading-[1.05] font-bold tracking-[-0.02em]">
              Payer guides for {statesCount} states
            </span>
            <span className="mt-8 inline-flex items-center gap-2 text-[0.95rem] font-semibold text-beacon-600">
              Open the index
              <span
                aria-hidden="true"
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-beacon-500 text-white transition-transform group-hover:translate-x-0.5"
              >
                <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 8h10M9 4l4 4-4 4" />
                </svg>
              </span>
            </span>
          </Link>
        </div>
      </Container>
    </section>
  );
}
